// Detects a failed dynamic import (the most common production trigger: a user
// holding stale index.html clicks a route whose hashed chunk no longer exists
// after a deploy) and recovers with exactly one automatic reload per session —
// a genuinely broken deploy must not reload-loop the tab.
const CHUNK_LOAD_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError/i;

const RELOAD_GUARD_KEY = "trainingportal:chunk-reload-attempted";

export function isChunkLoadError(error: Error): boolean {
  return CHUNK_LOAD_ERROR_PATTERN.test(error.message);
}

// Call once the app has successfully rendered (see the Suspense-scoped
// sentinel in App.tsx) so a later, unrelated chunk-load failure still gets
// its own single automatic-reload attempt.
export function clearChunkReloadGuard(): void {
  try {
    window.sessionStorage.removeItem(RELOAD_GUARD_KEY);
  } catch {
    // sessionStorage unavailable (private browsing, etc.) — nothing to clear.
  }
}

// Returns true if it triggered a reload. Safe to call for any error — non
// chunk-load errors and repeat chunk-load errors within the same guard window
// are both no-ops.
export function maybeAutoReloadOnChunkError(error: Error): boolean {
  if (!isChunkLoadError(error)) return false;
  try {
    if (window.sessionStorage.getItem(RELOAD_GUARD_KEY) === "1") return false;
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, "1");
  } catch {
    // Can't set the guard, so we can't safely promise a single reload —
    // fail safe to the manual "Reload page" fallback instead.
    return false;
  }
  window.location.reload();
  return true;
}
